import { Country, getCountries, getCountryCallingCode, isSupportedCountry } from "react-phone-number-input/input";
import countryNames from "react-phone-number-input/locale/en.json";
import { getRegionFromLocale, getUserTimeZone, userLocale } from "../../core/locale";

export const FALLBACK_PHONE_COUNTRY: Country = "GB";

export type CountryOption = {
  country: Country;
  name: string;
  dialCode: string;
};

type LocaleWithTimeZones = Intl.Locale & { getTimeZones?: () => string[]; timeZones?: string[] };

export const getDialCode = (country: Country) => `+${getCountryCallingCode(country)}`;

export const countryOptions: CountryOption[] = getCountries()
  .map((country) => ({ country, name: countryNames[country], dialCode: getDialCode(country) }))
  .sort((a, b) => a.name.localeCompare(b.name, "en"));

const getTimeZonesForCountry = (country: Country): string[] => {
  try {
    const locale = new Intl.Locale(`und-${country}`) as LocaleWithTimeZones;
    return locale.getTimeZones?.() ?? locale.timeZones ?? [];
  } catch {
    return [];
  }
};

let timeZoneCountries: Map<string, Country> | undefined;

const getTimeZoneCountries = () => {
  timeZoneCountries ??= new Map(getCountries().flatMap((country) => getTimeZonesForCountry(country).map((timeZone) => [timeZone, country] as const)));
  return timeZoneCountries;
};

const toCanonicalTimeZone = (timeZone: string): string | undefined => {
  try {
    return new Intl.DateTimeFormat("en", { timeZone }).resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
};

export const getCountryFromTimeZone = (timeZone: string | undefined): Country | undefined => {
  if (!timeZone) {
    return undefined;
  }
  const countries = getTimeZoneCountries();
  const canonical = toCanonicalTimeZone(timeZone);
  return countries.get(timeZone) ?? (canonical ? countries.get(canonical) : undefined);
};

export const getDefaultCountry = ({ timeZone = getUserTimeZone(), locale = userLocale }: { timeZone?: string; locale?: string } = {}): Country => {
  const timeZoneCountry = getCountryFromTimeZone(timeZone);
  if (timeZoneCountry) {
    return timeZoneCountry;
  }
  const region = getRegionFromLocale(locale);
  return region && isSupportedCountry(region) ? region : FALLBACK_PHONE_COUNTRY;
};
